const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const buildArticleExperience = (root) => {
  const prose = root.querySelector('[data-article-prose]');
  const reading = root.querySelector('[data-article-reading]');
  const desktopToc = root.querySelector('[data-article-toc-desktop]');
  const mobileToc = root.querySelector('[data-article-toc-mobile]');
  const desktopList = root.querySelector('[data-article-toc-list-desktop]');
  const mobileList = root.querySelector('[data-article-toc-list-mobile]');
  const mobileCount = root.querySelector('[data-article-toc-count]');

  if (!prose || !reading) return;

  /**
   * Wrap article tables in an accessible scroll shell.
   * The cue/fade is only exposed when the table genuinely overflows.
   */
  const tableShells = [];

  prose.querySelectorAll('table').forEach((table, index) => {
    if (table.closest('.gvs-article-table-shell')) return;

    const shell = document.createElement('div');
    shell.className = 'gvs-article-table-shell';

    const cue = document.createElement('div');
    cue.className = 'gvs-article-table-cue';
    cue.setAttribute('aria-hidden', 'true');
    cue.innerHTML = '<span>Swipe table</span><span>↔</span>';

    const wrapper = document.createElement('div');
    wrapper.className = 'gvs-article-table-scroll';
    wrapper.tabIndex = 0;
    wrapper.setAttribute('role', 'region');
    wrapper.setAttribute('aria-label', `Scrollable article table ${index + 1}`);

    table.parentNode?.insertBefore(shell, table);
    shell.append(cue, wrapper);
    wrapper.appendChild(table);

    const updateOverflowState = () => {
      const maxScrollLeft = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth);
      const overflowing = maxScrollLeft > 4;
      const atEnd = !overflowing || wrapper.scrollLeft >= maxScrollLeft - 4;

      shell.classList.toggle('is-overflowing', overflowing);
      shell.classList.toggle('is-at-end', atEnd);
      cue.hidden = !overflowing;
    };

    wrapper.addEventListener('scroll', updateOverflowState, { passive: true });
    tableShells.push({ shell, wrapper, updateOverflowState });

    requestAnimationFrame(updateOverflowState);
  });

  if (tableShells.length) {
    const refreshTables = () => tableShells.forEach(({ updateOverflowState }) => updateOverflowState());

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(refreshTables);
      tableShells.forEach(({ wrapper }) => resizeObserver.observe(wrapper));
    } else {
      window.addEventListener('resize', refreshTables, { passive: true });
    }
  }

  const headings = [...prose.querySelectorAll('h2')].filter((heading) => heading.textContent?.trim());
  const minimum = Number.parseInt(root.dataset.tocMin || '4', 10);

  if (headings.length < minimum || !desktopToc || !mobileToc || !desktopList || !mobileList) return;

  const usedIds = new Set();
  const links = [];

  headings.forEach((heading, index) => {
    const text = heading.textContent.trim();
    let id = heading.id?.trim() || slugify(text) || `section-${index + 1}`;
    const base = id;
    let suffix = 2;

    while (usedIds.has(id) || (document.getElementById(id) && document.getElementById(id) !== heading)) {
      id = `${base}-${suffix}`;
      suffix += 1;
    }

    usedIds.add(id);
    heading.id = id;

    const item = document.createElement('li');
    const link = document.createElement('a');
    link.href = `#${id}`;
    link.textContent = text;
    item.appendChild(link);
    desktopList.appendChild(item);

    const mobileItem = item.cloneNode(true);
    mobileList.appendChild(mobileItem);

    links.push({ heading, desktopLink: link });
  });

  reading.classList.add('has-toc');
  desktopToc.hidden = false;
  mobileToc.hidden = false;

  if (mobileCount) {
    mobileCount.textContent = `${headings.length} ${headings.length === 1 ? 'section' : 'sections'}`;
  }

  // Close the mobile contents panel as soon as a destination is chosen.
  mobileList.addEventListener('click', (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;

    mobileToc.open = false;
  });

  /**
   * IDs are generated client-side, so the browser's initial native hash jump
   * may happen before the matching H2 exists. Re-run the jump once IDs exist.
   */
  const followCurrentHash = () => {
    if (!window.location.hash) return;

    let targetId = window.location.hash.slice(1);
    try {
      targetId = decodeURIComponent(targetId);
    } catch {
      // Keep the undecoded fragment if it is malformed.
    }

    const target = document.getElementById(targetId);
    if (!target || !prose.contains(target)) return;

    requestAnimationFrame(() => {
      target.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  };

  followCurrentHash();
  window.addEventListener('hashchange', followCurrentHash);

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

      if (!visible.length) return;

      const activeId = visible[0].target.id;
      links.forEach(({ heading, desktopLink }) => {
        const active = heading.id === activeId;
        desktopLink.classList.toggle('is-active', active);

        if (active) {
          desktopLink.setAttribute('aria-current', 'true');

          const scrollRegion = desktopList.closest('nav');
          if (scrollRegion && scrollRegion.scrollHeight > scrollRegion.clientHeight) {
            const regionRect = scrollRegion.getBoundingClientRect();
            const linkRect = desktopLink.getBoundingClientRect();
            const inset = 8;
            let nextTop = null;

            if (linkRect.top < regionRect.top + inset) {
              nextTop = scrollRegion.scrollTop + (linkRect.top - regionRect.top) - inset;
            } else if (linkRect.bottom > regionRect.bottom - inset) {
              nextTop = scrollRegion.scrollTop + (linkRect.bottom - regionRect.bottom) + inset;
            }

            if (nextTop !== null) {
              scrollRegion.scrollTo({
                top: Math.max(0, nextTop),
                behavior: prefersReducedMotion() ? 'auto' : 'smooth',
              });
            }
          }
        } else {
          desktopLink.removeAttribute('aria-current');
        }
      });
    },
    { rootMargin: '-18% 0px -68% 0px', threshold: [0, 1] }
  );

  headings.forEach((heading) => observer.observe(heading));
};

document.querySelectorAll('[data-gvs-article-experience]').forEach(buildArticleExperience);
